import React from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Avatar,
    Stack,
    Grid,
    useTheme,
    Box as MuiBox,
    useMediaQuery
} from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import SpeedIcon from "@mui/icons-material/Speed";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";

const features = [
    {
        title: "Expert Curriculum",
        description: "Learn from industry experts with courses designed to meet modern job market demands.",
        icon: <CodeIcon sx={{ fontSize: 32 }} />,
        color: "#e50914",
        bgGradient: "linear-gradient(135deg, #e50914 0%, #ff4b2b 100%)",
    },
    {
        title: "Project-Based Learning",
        description: "Build real-world projects that you can showcase in your portfolio and use in interviews.",
        icon: <LaptopMacIcon sx={{ fontSize: 32 }} />,
        color: "#6a0dad",
        bgGradient: "linear-gradient(135deg, #6a0dad 0%, #A380FF 100%)",
    },
    {
        title: "Fast-Track Career",
        description: "Accelerate your transition into tech with structured paths and high-intensity learning.",
        icon: <SpeedIcon sx={{ fontSize: 32 }} />,
        color: "#00e676",
        bgGradient: "linear-gradient(135deg, #00e676 0%, #00c853 100%)",
    },
    {
        title: "AI-Powered Insights",
        description: "Our platform uses AI to help guide your learning and provide personalized feedback.",
        icon: <AutoAwesomeIcon sx={{ fontSize: 32 }} />,
        color: "#A380FF",
        bgGradient: "linear-gradient(135deg, #A380FF 0%, #6a0dad 100%)",
    },
];

const WhyChoose = ({ colors, isDark }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));

    const settings = {
        dots: true,
        infinite: true,
        speed: 800,
        slidesToShow: isMobile ? 1 : isTablet ? 2 : 3,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 5000,
        pauseOnHover: true,
        arrows: false,
        cssEase: "cubic-bezier(0.4, 0, 0.2, 1)",
        appendDots: dots => (
            <Box sx={{ mt: 4 }}>
                <ul style={{ margin: "0px", padding: "0px" }}> {dots} </ul>
            </Box>
        ),
    };

    return (
        <Box
            sx={{
                py: { xs: 8, md: 15 },
                position: "relative",
                overflow: "hidden",
                backgroundColor: isDark ? "#0a0a0a" : "#fafafa",
            }}
        >
            {/* 🧩 Background Decorative Elements */}
            <MuiBox
                sx={{
                    position: "absolute",
                    top: "10%",
                    right: "2%",
                    width: "350px",
                    height: "350px",
                    background: "radial-gradient(circle, rgba(106,13,173,0.06) 0%, transparent 70%)",
                    filter: "blur(70px)",
                    zIndex: 0,
                }}
            />

            <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                <Box sx={{ textAlign: "center", mb: { xs: 6, md: 10 } }}>
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <AutoAwesomeIcon sx={{ color: colors.primary, fontSize: 18 }} />
                        <Typography variant="overline" sx={{ letterSpacing: 3, fontWeight: 900, color: "text.secondary" }}>
                            CORE PHILOSOPHY
                        </Typography>
                    </Stack>

                    <Typography
                        variant="h2"
                        fontWeight={900}
                        sx={{
                            mb: 3,
                            fontSize: { xs: "2.2rem", md: "3.2rem" },
                            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            letterSpacing: "-0.03em",
                        }}
                    >
                        Why Choose CodingComet?
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
                            maxWidth: 650,
                            mx: "auto",
                            fontSize: "1.1rem"
                        }}
                    >
                        Engineered for excellence. Designed for your success.
                    </Typography>
                </Box>

                <Box sx={{
                    mx: -2,
                    "& .slick-list": { py: 5 },
                    "& .slick-dots li button:before": {
                        color: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)",
                        fontSize: "10px"
                    },
                    "& .slick-dots li.slick-active button:before": { color: colors.primary },
                }}>
                    <Slider {...settings}>
                        {features.map((feature, index) => (
                            <Box key={index} sx={{ px: 2 }}>
                                <Card
                                    sx={{
                                        height: "100%",
                                        borderRadius: "28px",
                                        background: isDark ? "rgba(255,255,255,0.02)" : "#ffffff",
                                        backdropFilter: "blur(20px)",
                                        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                                        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                                        position: "relative",
                                        "&:hover": {
                                            transform: "translateY(-12px)",
                                            boxShadow: `0 30px 60px -15px ${isDark ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.1)"}`,
                                            borderColor: feature.color,
                                            "& .feature-box": {
                                                background: feature.bgGradient,
                                                color: "#fff"
                                            },
                                            "& .feature-icon": {
                                                transform: "scale(1.25) rotate(5deg)"
                                            }
                                        },
                                    }}
                                >
                                    <CardContent sx={{ p: { xs: 4, md: 5 }, textAlign: "center" }}>
                                        <Avatar
                                            className="feature-box"
                                            sx={{
                                                width: 72,
                                                height: 72,
                                                mx: "auto",
                                                mb: 4,
                                                background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                                                color: feature.color,
                                                transition: "all 0.3s ease",
                                            }}
                                        >
                                            <Box className="feature-icon" sx={{ transition: "all 0.3s ease", display: "flex" }}>
                                                {feature.icon}
                                            </Box>
                                        </Avatar>

                                        <Typography variant="h5" fontWeight={800} sx={{ mb: 2, fontSize: "1.4rem" }}>
                                            {feature.title}
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: "text.secondary",
                                                lineHeight: 1.8,
                                                fontSize: "1rem"
                                            }}
                                        >
                                            {feature.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Box>
                        ))}
                    </Slider>
                </Box>

                {/* 📊 Fact Row */}
                <Box sx={{ mt: 8, pt: 6, borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}` }}>
                    <Grid container spacing={4} justifyContent="center" textAlign="center">
                        {[
                            { label: "Active Students", value: "25k+" },
                            { label: "Course Completions", value: "120k+" },
                            { label: "Avg Salary Hike", value: "65%" },
                            { label: "Placement Rate", value: "92%" }
                        ].map((stat) => (
                            <Grid size={{ xs: 6, md: 3 }} key={stat.label}>
                                <Typography variant="h3" fontWeight={900} color={colors.primary} sx={{ fontSize: { xs: "2rem", md: "3rem" } }}>
                                    {stat.value}
                                </Typography>
                                <Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: 2, fontWeight: 700, opacity: 0.6 }}>
                                    {stat.label}
                                </Typography>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Container>
        </Box>
    );
};

export default WhyChoose;
